import {
  type DatabaseAccessType,
  type FunctionDefinition,
  type RouteDefinition,
  shared,
  tokenRequestBodySchema,
  tokenResponseBodySchema,
  Integration,
} from '@tig-dpqa-cloud/contract-backend-common';

import { type PermissionType, Permissions } from './permissions';
import {
  BackofficeUserCreationBody,
  BackofficeUserDeleteParamsSchema,
  BackofficeUserPatchBodySchema,
  BackofficeUserPatchParamsSchema,
  BackofficeUsersGetResponseSchema,
  BackofficeUsersPatchResponseSchema,
  BackofficeUsersPostResponseSchema,
  dataplaneListResponse,
  DataplaneRequestQueryPath,
  dataplaneResponse,
  dataplaneVersionsResponse,
  DefaultGenericAllowanceConfigResponse,
  IntegrationListResponse,
  IntegrationPostRequestBody,
  IntegrationRequestParams,
  TenantCreateBody,
  TenantGetResponse,
  TenantListResponse,
  TenantMetadataConfigCreateResponse,
  TenantMetadataConfigCreateRequestBody,
  GenericAnonRecordsGetPathParams,
  GenericAnonRecordsGetQuery,
  GenericAnonRecordsGetResponse,
  TenantsPostResponse,
  TenantUpdateBody,
} from './schemas';
import z from 'zod';
import { UserContextGetResponseSchema } from './schemas/user-context-get';

export const authorizer: FunctionDefinition = {
  infra: {
    entrypointRelativeToFunctionsFolder: './authorizers/src/backoffice/index.ts',
    dbAccess: 'BYPASS',
    needsEgress: true,
  },
};

export const asyncFunctions = {} as const satisfies { [key: string]: FunctionDefinition };

/**
 * ! BE AWARE
 * When creating the IAM policy, there is no way to have an "internal" wildcard, meaning that a * on the resource, immediately includes all postfixes.
 * We have to be aware of this, and how the paths will convert to a resource in the authorizer.
 * From the first route parameter, everything will be wildcards:
 *  * Pick your paths smart and according to the permissions. Make sure that no arns would be constructed that are very permissive
 *  * The HTTP method is part of the ARN, so two paths that will be mapped to the same resource path, will still have different arns.
 * For clarity's sake, the function that will convert the paths for the authorizer is also defined below. See examples in the test file.
 */
export const routes = {
  postToken: {
    path: '/token',
    method: 'post',
    request: {
      body: tokenRequestBodySchema,
    },
    unauthenticated: true,
    successResponse: {
      description: 'The requested tokens',
      jsonSchema: tokenResponseBodySchema,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './authentication/src/token-post/index.ts',
      dbAccess: 'NOACCESS',
      needsEgress: true,
    },
  },
  getTenantList: {
    path: '/tenants',
    method: 'get',
    successResponse: {
      description: 'The list of tenants',
      jsonSchema: TenantListResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/tenants-get/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  getTenant: {
    path: '/tenants/{tenantId}',
    method: 'get',
    successResponse: {
      description: 'The tenant',
      jsonSchema: TenantGetResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/tenant-get/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  postTenant: {
    path: '/tenants',
    method: 'post',
    request: {
      body: TenantCreateBody,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/tenant-post/index.ts',
      dbAccess: 'BYPASS',
    },
    successResponse: {
      description: 'The created tenant',
      code: '201',
      jsonSchema: TenantsPostResponse,
    },
  },
  putTenant: {
    path: '/tenants/{tenantId}',
    method: 'put',
    request: {
      body: TenantUpdateBody,
      params: z.object({
        tenantId: shared.TenantId,
      }),
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/tenant-put/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  getDataplanes: {
    path: '/tenants/{tenantId}/dataplanes',
    method: 'get',
    request: {
      params: z.object({
        tenantId: shared.TenantId,
      }),
    },
    successResponse: {
      description: 'The list of dataplanes for the tenant',
      jsonSchema: dataplaneListResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/dataplanes-get/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  getDataplaneVersions: {
    path: '/dataplanes/versions',
    method: 'get',
    successResponse: {
      description: 'The list of available dataplane versions',
      jsonSchema: dataplaneVersionsResponse,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/dataplane-versions-get/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  getUserContext: {
    path: '/user/context',
    method: 'get',
    successResponse: {
      description: 'The context for the authenticate user',
      jsonSchema: UserContextGetResponseSchema,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/context-get/backoffice/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  getUsers: {
    path: '/users',
    method: 'get',
    successResponse: {
      description: 'The list of backoffice users',
      jsonSchema: BackofficeUsersGetResponseSchema,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/users-get/backoffice/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  postUsers: {
    path: '/users',
    method: 'post',
    request: {
      body: BackofficeUserCreationBody,
    },
    successResponse: {
      description: 'The created backoffice user',
      code: '201',
      jsonSchema: BackofficeUsersPostResponseSchema,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/users-post/backoffice/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  patchUsers: {
    path: '/users/{id}',
    method: 'patch',
    request: {
      params: BackofficeUserPatchParamsSchema,
      body: BackofficeUserPatchBodySchema,
    },
    successResponse: {
      description: 'The updated backoffice user',
      jsonSchema: BackofficeUsersPatchResponseSchema,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/users-patch/backoffice/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  deleteUsers: {
    path: '/users/{id}',
    method: 'delete',
    request: {
      params: BackofficeUserDeleteParamsSchema,
    },
    infra: {
      entrypointRelativeToFunctionsFolder: './users/src/users-delete/backoffice/index.ts',
      dbAccess: 'BYPASS',
    },
  },
  deleteDataplane: {
    infra: {
      entrypointRelativeToFunctionsFolder: './dataplanes/src/dataplane-delete/index.ts',
      dbAccess: 'BYPASS',
    },
    path: '/dataplanes/{dataplaneId}',
    method: 'delete',
    description: 'Revoke a dataplane, ensuring it can no longer connect to the cloud',
    request: {
      params: DataplaneRequestQueryPath,
    },
    successResponse: {
      description: 'Dataplane revoked successfully',
      code: '200',
      jsonSchema: dataplaneResponse,
    },
  },
  getIntegrations: {
    infra: {
      entrypointRelativeToFunctionsFolder: './integrations/src/integrations-get/index.ts',
      dbAccess: 'BYPASS',
    },
    path: '/integrations',
    method: 'get',
    description: 'Get the list of available integrators',
    successResponse: {
      code: '200',
      description: 'List of integrators retrieved successfully',
      jsonSchema: IntegrationListResponse,
    },
  },
  getIntegration: {
    infra: {
      entrypointRelativeToFunctionsFolder: './integrations/src/integration-get/index.ts',
      dbAccess: 'BYPASS',
    },
    path: '/integrations/{integrator}/{version}',
    method: 'get',
    description: 'Get a specific integrator',
    request: {
      params: IntegrationRequestParams,
    },
    successResponse: {
      code: '200',
      description: 'Integrator retrieved successfully',
      jsonSchema: Integration,
    },
  },
  putIntegrations: {
    infra: {
      entrypointRelativeToFunctionsFolder: './integrations/src/integration-put/index.ts',
      dbAccess: 'BYPASS',
    },
    path: '/integrations/{integrator}/{version}',
    method: 'put',
    description: 'Update an existing integrator',
    request: {
      params: IntegrationRequestParams,
    },
  },
  postIntegrations: {
    infra: {
      entrypointRelativeToFunctionsFolder: './integrations/src/integration-post/index.ts',
      dbAccess: 'BYPASS',
    },
    path: '/integrations',
    method: 'post',
    description: 'Create a new integrator',
    request: {
      body: IntegrationPostRequestBody,
    },
  },
  getMetadataConfigCredentials: {
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/tenant-metadata-config-creds-get/index.ts',
      dbAccess: 'BYPASS',
    },
    path: '/tenants/{tenantId}/metadata-config/credentials',
    method: 'get',
    request: {
      params: z.object({
        tenantId: shared.TenantId,
      }),
    },
    successResponse: {
      description: 'The credentials for accessing the metadata configuration',
      jsonSchema: shared.BucketCredentialsWithKey,
    },
  },
  postMetadataConfig: {
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/tenant-metadata-config-post/index.ts',
      dbAccess: 'BYPASS',
    },
    path: '/tenants/{tenantId}/metadata-config',
    method: 'post',
    description: 'Upload a new metadata config and update the tenant for the metadata config reference',
    request: {
      params: z.object({
        tenantId: shared.TenantId,
      }),
      body: TenantMetadataConfigCreateRequestBody,
    },
    successResponse: {
      description: 'Metadata config uploaded and tenant reference updated successfully',
      code: '201',
      jsonSchema: TenantMetadataConfigCreateResponse,
    },
  },
  getGenericAnonRecords: {
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/generic-anon-records-get/index.ts',
      dbAccess: 'BYPASS',
    },
    path: '/tenants/{tenantId}/generic-anon-records',
    method: 'get',
    description: 'Retrieve generic anonymization records with optional filters',
    request: {
      params: GenericAnonRecordsGetPathParams,
      query: GenericAnonRecordsGetQuery,
    },
    successResponse: {
      description: 'The list of generic anonymization records',
      jsonSchema: GenericAnonRecordsGetResponse,
    },
  },
  getDefaultGenericAllowanceConfig: {
    infra: {
      entrypointRelativeToFunctionsFolder: './tenants/src/default-generic-allowance-config-get/index.ts',
      dbAccess: 'NOACCESS',
    },
    path: '/generic-allowances/default-config',
    method: 'get',
    description: 'Preview the default generic-anonymization allowance template used for new tenants',
    successResponse: {
      description: 'The default generic allowance config, if one is configured for this environment',
      jsonSchema: DefaultGenericAllowanceConfigResponse,
    },
  },
} as const satisfies {
  [key: string]: RouteDefinition & {
    infra: {
      dbAccess: Extract<DatabaseAccessType, 'BYPASS' | 'NOACCESS'>;
    };
  };
};

export type Functions = keyof typeof routes | keyof typeof asyncFunctions | 'authorizer';

export const permissionsToRoutesMap: Record<PermissionType, RouteDefinition[]> = {
  [Permissions.enum.DEFAULT]: [routes.getUserContext, routes.postToken],
  [Permissions.enum.TENANT_READ]: [
    routes.getTenant,
    routes.getTenantList,
    routes.getDataplanes,
    routes.getDataplaneVersions,
    routes.getMetadataConfigCredentials,
    routes.getGenericAnonRecords,
    routes.getDefaultGenericAllowanceConfig,
  ],
  [Permissions.enum.TENANT_WRITE]: [
    routes.postTenant,
    routes.putTenant,
    routes.deleteDataplane,
    routes.postMetadataConfig,
  ],
  [Permissions.enum.INTEGRATION_READ]: [routes.getIntegrations, routes.getIntegration],
  [Permissions.enum.INTEGRATION_WRITE]: [routes.putIntegrations, routes.postIntegrations],
  [Permissions.enum.USER_MANAGEMENT]: [routes.getUsers, routes.postUsers, routes.patchUsers, routes.deleteUsers],
};

export const convertToArnResourcePostfix = ({ path, method }: RouteDefinition) =>
  `${method.toUpperCase()}${path.replace(/\{.*$/, '*')}`;
